import React, { useEffect, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid, CircularProgress, Menu, MenuItem } from '@material-ui/core'
import { BlurOff, BlurOn, MoreHoriz } from '@material-ui/icons'
import VideoCameraIcon from 'components/common/icons/VideoCameraIcon'
import DoctorCamPlaceholder from './DoctorCamPlaceholder'
import useVirtualBackground from 'hooks/useVirtualBackground'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { VB_MODES } from 'consts/virtualBackgroundConsts'
import { trackEvent } from 'utils/analyticsUtils'

const CAMERA_SIZE = 150

const useStyles = ({ isCapturing, showCanvas }) =>
  makeStyles({
    cameraVideo: {
      width: CAMERA_SIZE,
      height: CAMERA_SIZE,
      borderRadius: 100,
      objectFit: 'cover',
      position: 'absolute',
      bottom: 0,
      opacity: showCanvas ? 0 : 1,
      pointerEvents: 'none',
      transition: 'opacity 0.15s ease'
    },
    cameraCanvas: {
      width: CAMERA_SIZE,
      height: CAMERA_SIZE,
      borderRadius: 100,
      objectFit: 'cover',
      position: 'absolute',
      bottom: 0,
      pointerEvents: 'none'
    },
    camVideoAndIconContainer: {
      position: 'absolute',
      top: isCapturing ? '23px' : '20px',
      left: isCapturing ? 0 : '10px',
      width: CAMERA_SIZE,
      height: CAMERA_SIZE,
      zIndex: 2000
    },
    controlsMenuContainer: {
      position: 'absolute',
      bottom: 0,
      left: 18,
      zIndex: 2001
    },
    cameraIconContainer: {
      width: 40,
      height: 40,
      boxShadow: '0px 4px 30px rgba(0, 0, 0, 0.14)',
      background: 'white',
      padding: 0,
      border: '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: 30,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer'
    },
    cameraIconContainerActive: {
      background: '#3c52ef',
      borderColor: '#3c52ef',
      boxShadow: '0px 4px 30px rgba(60, 82, 239, 0.5)'
    },
    menuItem: {
      height: 40,
      padding: '4px 6px 4px 0px',
      '&:hover': {
        backgroundColor: 'var(--bg-color-14)'
      }
    },
    menuPaper: {
      padding: 0,
      boxShadow: '0px 4px 30px rgba(0, 0, 0, 0.15)'
    },
    menuList: {
      padding: 0
    },
    menuItemIcon: {
      paddingTop: 2,
      paddingRight: 4,
      width: 36,
      textAlign: 'center'
    },
    menuItemText: { fontSize: 13, lineHeight: '16px' }
  })({ isCapturing, showCanvas })

const stopEvent = event => {
  event.stopPropagation()
  event.preventDefault()
}

const DoctorCameraControlsMenu = ({
  classes,
  scanId,
  isCameraOn,
  onToggleCameraOption,
  virtualBackgroundFF,
  isSupported,
  isLoading,
  mode,
  setMode
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const isBlurActive = mode === VB_MODES.BLUR
  const isBlurEnabled = virtualBackgroundFF && isCameraOn && isSupported

  const handleOpen = useCallback(
    event => {
      stopEvent(event)
      setAnchorEl(event.currentTarget)

      trackEvent('ScanReview_VirtualBackground_MenuOpened', {
        scanId,
        mode,
        isCameraOn,
        isSupported,
        isLoading
      })
    },
    [scanId, mode, isCameraOn, isSupported, isLoading]
  )

  const handleClose = useCallback(event => {
    if (event) {
      stopEvent(event)
    }
    setAnchorEl(null)
  }, [])

  const handleToggleCamera = useCallback(
    event => {
      stopEvent(event)
      onToggleCameraOption(!isCameraOn)
      setAnchorEl(null)

      trackEvent('ScanReview_VirtualBackground_CameraToggled', {
        scanId,
        toCameraOn: !isCameraOn,
        mode
      })
    },
    [scanId, mode, isCameraOn, onToggleCameraOption]
  )

  const handleToggleBlur = useCallback(
    event => {
      stopEvent(event)
      if (!isBlurEnabled) {
        return
      }
      setMode(isBlurActive ? VB_MODES.OFF : VB_MODES.BLUR, { source: 'menu' })
      setAnchorEl(null)
    },
    [isBlurActive, isBlurEnabled, setMode]
  )

  return (
    <>
      <button
        type="button"
        className={`${classes.cameraIconContainer} ${isBlurActive ? classes.cameraIconContainerActive : ''}`}
        aria-label="Camera options"
        onClick={handleOpen}
      >
        <MoreHoriz style={{ color: isBlurActive ? '#fff' : '#666' }} />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disablePortal
        classes={{ paper: classes.menuPaper, list: classes.menuList }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleToggleCamera} className={classes.menuItem}>
          <Grid container alignItems="center" style={{ height: 40 }}>
            <Grid item className={classes.menuItemIcon}>
              <VideoCameraIcon cameraOff={!isCameraOn} size="20" />
            </Grid>
            <Grid item className={classes.menuItemText}>
              {t(`dialogs.scanReviewEditor.${isCameraOn ? 'turnCamOff' : 'turnCamOn'}`)}
            </Grid>
          </Grid>
        </MenuItem>

        {virtualBackgroundFF && (
          <MenuItem onClick={handleToggleBlur} className={classes.menuItem} disabled={!isBlurEnabled}>
            <Grid container alignItems="center" style={{ height: 40 }}>
              <Grid item className={classes.menuItemIcon}>
                {isLoading ? (
                  <CircularProgress size={16} style={{ color: '#3c52ef' }} />
                ) : isBlurActive ? (
                  <BlurOn fontSize="small" style={{ color: '#3c52ef' }} />
                ) : (
                  <BlurOff fontSize="small" style={{ color: '#666' }} />
                )}
              </Grid>
              <Grid item className={classes.menuItemText}>
                {t('dialogs.scanReviewEditor.virtualBackground.toggle')}
              </Grid>
            </Grid>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

const DoctorCamera = ({ scanId, isCapturing, cameraElement, isCameraOn, onToggleCameraOption }) => {
  const featureFlags = useFeatureFlags()
  const { virtualBackgroundFF } = featureFlags

  const { mode, setMode, isLoading, isInitialized, isSupported, canvasRef, initSegmenter, isActive } =
    useVirtualBackground({
      videoElement: cameraElement?.current,
      enabled: virtualBackgroundFF,
      scanId
    })

  const showCanvas = virtualBackgroundFF && isActive && isSupported && isInitialized
  const classes = useStyles({ isCapturing, showCanvas })

  // Initialize canvas size when ref is available
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = CAMERA_SIZE
      canvasRef.current.height = CAMERA_SIZE
    }
  }, [canvasRef])

  // Initialize segmenter when camera is turned on and VB is enabled
  useEffect(() => {
    if (isCameraOn && virtualBackgroundFF && isSupported && !isLoading) {
      initSegmenter()
    }
  }, [isCameraOn, virtualBackgroundFF, isSupported, initSegmenter, isLoading])

  // Reset VB mode when camera is turned off
  useEffect(() => {
    if (!isCameraOn && mode !== VB_MODES.OFF) {
      setMode(VB_MODES.OFF, { source: 'camera_off' })
    }
  }, [isCameraOn, mode, setMode])

  const handleVideoRef = useCallback(
    input => {
      if (cameraElement) {
        cameraElement.current = input
      }
    },
    [cameraElement]
  )

  return (
    <div className={classes.camVideoAndIconContainer}>
      {/* Video element - hidden when VB is active, but still receives the stream */}
      <video ref={handleVideoRef} autoPlay muted className={classes.cameraVideo} />

      {/* Canvas element - shown when VB is active */}
      {virtualBackgroundFF && isSupported && (
        <canvas ref={canvasRef} className={classes.cameraCanvas} style={{ display: showCanvas ? 'block' : 'none' }} />
      )}

      <DoctorCamPlaceholder cameraOn={isCameraOn} />

      {/* Controls menu (camera + blur) */}
      <div className={classes.controlsMenuContainer}>
        <DoctorCameraControlsMenu
          classes={classes}
          scanId={scanId}
          isCameraOn={isCameraOn}
          onToggleCameraOption={onToggleCameraOption}
          virtualBackgroundFF={virtualBackgroundFF}
          isSupported={isSupported}
          isLoading={isLoading}
          mode={mode}
          setMode={setMode}
        />
      </div>
    </div>
  )
}

export default DoctorCamera
